import { useEffect, type ReactNode } from "react";
import type { ChallengeResponse, SentPuzzleResponse } from "@versedrill/core";
import { Ladder } from "@/components/Ladder.tsx";
import { Masthead } from "@/components/Masthead.tsx";
import { ErrorNote, LoadingLeaf } from "@/components/Notices.tsx";
import { ScriptureNotice } from "@/components/ScriptureNotice.tsx";
import { VerseCard } from "@/components/VerseCard.tsx";
import { Welcome, type WelcomeCopy } from "@/components/Welcome.tsx";
import type { GameSource } from "@/lib/api.ts";
import { blotsVisible, displayText } from "@/lib/game.ts";
import { useStore } from "@/lib/store.ts";
import { useGame, type GameResult } from "@/lib/useGame.ts";

export interface ResultContext {
  result: GameResult;
  /** The verse as shown ON SCREEN: the real text once solved. Never send this anywhere; share `challenge.text`. */
  shownText: string;
  /** `challenge.text` is always the redacted text, so it is the only text that is safe to share. */
  challenge: ChallengeResponse;
}

/** One playable game: the sticky verse, the three rounds, and whatever result screen the caller supplies. */
export function GameView({
  source,
  onFinished,
  renderResult,
  welcome,
  previousStars,
  unavailable,
}: {
  source: GameSource;
  onFinished: (r: GameResult) => void;
  renderResult: (ctx: ResultContext) => ReactNode;
  /** Copy for the welcome card; may depend on the puzzle (a sent verse names its sender). */
  welcome?: WelcomeCopy | ((challenge: ChallengeResponse | SentPuzzleResponse) => WelcomeCopy);
  previousStars?: number;
  /** Shown instead of an error when the game doesn't exist (a sent verse that expired or was retracted). */
  unavailable?: ReactNode;
}) {
  const g = useGame(source, onFinished);
  const { challenge, state, result, start } = g;
  const seenIntro = useStore((s) => s.seenIntro);
  const markIntroSeen = useStore((s) => s.markIntroSeen);
  const anyPlayed = useStore((s) => Object.keys(s.stars).length + Object.keys(s.received).length > 0);
  const returning = seenIntro || anyPlayed;

  // Returning players go straight in; first-timers get the welcome card.
  const ready = Boolean(challenge && g.config);
  useEffect(() => {
    if (ready && !state.started && returning) start();
  }, [ready, state.started, returning, start]);

  // A sent verse carries its sender; a daily game doesn't (undefined), which is how the header tells them apart.
  const fromName = challenge && "code" in challenge && challenge.code ? (challenge as SentPuzzleResponse).from : undefined;
  const solved = Boolean(result);
  const shownText = challenge ? displayText({ redacted: challenge.text, unredacted: g.unredacted, redactionOn: state.redactionOn, solvedText: result?.text ?? null }) : "";

  return (
    <div className="min-h-dvh">
      <Masthead />
      {g.loadError && g.loadStatus === 404 && unavailable ? (
        <main className="mx-auto max-w-2xl px-5 pt-8 sm:px-8">{unavailable}</main>
      ) : g.loadError ? (
        <div className="mx-auto max-w-2xl px-5 pt-8 sm:px-8">
          <ErrorNote message={g.loadError} onRetry={g.reload} />
        </div>
      ) : !challenge ? (
        <LoadingLeaf />
      ) : (
        <>
          <VerseCard challenge={challenge} text={shownText} redactionOn={state.redactionOn} solved={solved} score={state.score} onReveal={g.revealNames} fromName={fromName} />
          <main className="mx-auto max-w-2xl px-5 pb-24 pt-8 sm:px-8">
            {!state.started ? (
              <Welcome
                copy={typeof welcome === "function" ? welcome(challenge) : welcome}
                previousStars={previousStars}
                onBegin={() => {
                  markIntroSeen();
                  start();
                }}
              />
            ) : (
              <div className="flex flex-col gap-10">
                {g.error && <ErrorNote message={g.error} onDismiss={g.dismissError} />}
                <Ladder
                  state={state}
                  hintsAllowed={challenge.hints}
                  disabled={g.busy || state.phase !== "playing"}
                  foundText={shownText}
                  redactionOn={blotsVisible(state.redactionOn, solved)}
                  onPick={g.pick}
                  onHint={g.hint}
                />
                {result && renderResult({ result, shownText, challenge })}
                <ScriptureNotice />
              </div>
            )}
          </main>
        </>
      )}
      <p className="sr-only" role="status" aria-live="polite">
        {g.announcement}
      </p>
    </div>
  );
}
